import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { goBack, CallHistoryMethodAction } from 'connected-react-router';

import moment from 'moment';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import BackIcon from '@components/basics/icons/back';
import Typography from '@components/basics/typography';
import DateIcon from '@components/basics/icons/date-icon';
import MotionOpacity from '@components/motion/motion-opacity';
import { Invoice } from '@services/payment/types/invoice';

import ArtistInvoiceItem from './invoice-item';

interface ArtistInvoicesProps {
  className?: string;
  artistName: string;
  invoices: Invoice[];
  goBack: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => {
  let invoiceList: Invoice[] = [];
  const summary = state?.subscriptionManager.summary;
  if (summary) {
    summary.artists.forEach(({ subscriptions }) => {
      if (subscriptions?.length) {
        subscriptions.forEach(({ invoices }) => {
          if (invoices?.length) {
            invoiceList = [...invoiceList, ...invoices];
          }
        });
      }
    });
    summary.recordLabels.forEach(({ subscriptions }) => {
      if (subscriptions?.length) {
        subscriptions.forEach(({ invoices }) => {
          if (invoices?.length) {
            invoiceList = [...invoiceList, ...invoices];
          }
        });
      }
    });
  }
  return {
    invoices: invoiceList,
    artistName: state.artist.active?.name,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goBack: () => dispatch(goBack()),
});

export const ArtistInvoices: React.FC<ArtistInvoicesProps> = ({
  className,
  artistName,
  invoices,
  goBack,
}) => {
  return (
    <MotionOpacity className={className}>
      <BackIcon className="back-icon" onClick={() => goBack()} />
      <Typography
        className="mb-30"
        type="subheader"
        weight="400"
        customFont="semi-bold"
      >
        <Translate
          id="ARTIST_PROFILE.INVOICES.TITLE"
          data={{ artistName: artistName }}
        />
      </Typography>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        className="mb-30"
      >
        <DateWrapper>
          <DateIcon isInvertedTheme />
          <Typography className="date-text" weight="700">
            {moment(new Date()).format("MMM 'YY")}
          </Typography>
        </DateWrapper>
      </Flex>
      {!!invoices.length && (
        <Box>
          {invoices.reduce((acc: React.ReactNode[], invoice, index) => {
            acc.push(
              <ArtistInvoiceItem
                key={invoice.externalId}
                number={invoice.externalId}
                isPaid={invoice.status === 'paid' && !!invoice.url}
                price={invoice.amount}
                type={invoice.description}
                date={invoice.invoice_date}
                onClick={() => (invoice.url ? window.open(invoice.url) : null)}
              />
            );
            if (index !== invoices.length - 1) {
              acc.push(<Separator key={invoice.externalId + index + 'sep'} />);
            }
            return acc;
          }, [])}
        </Box>
      )}
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ArtistInvoices)`
  .back-icon {
    margin-bottom: 24px;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      margin-bottom: 0;
      position: absolute;
      top: 0;
      left: 0;
    }
  }
`);

const DateWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.primary};
  padding: 16px 18px;
  border-radius: 100px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 115px;

  .date-text {
    color: ${({ theme }) => theme.colors.background};
  }
`;

const Separator = styled.hr`
  height: 1px;
  width: 100%;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  margin: 20px 0;
  display: block;
`;
